<template>
    <div class="full-height">
        <pick-price-drive :selected-index="0" />

        <b-container fluid>
            <b-row>
                <b-col>
                    <b-breadcrumb :items="breadcrumbs" class="mb-sm-0" />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-row>
                        <b-col>
                            <h1 class="text-center">
                                {{ title }}
                            </h1>
                        </b-col>
                    </b-row>

                    <b-row class="main-content">
                        <b-col
                            ref="vehicleDetails"
                            sm="5"
                            order-sm="1"
                            class="vehicle-details"
                        >
                            <div v-if="isLoading && firstLoad" class="w-100">
                                <vehicle-card-loader />
                            </div>

                            <affix
                                v-else
                                relative-element-selector=".left-content"
                                :style="{
                                    width: enableAffix ? affixWidth : ''
                                }"
                                :enabled="enableAffix"
                                :offset="{ top: 60, bottom: 0 }"
                            >
                                <vehicle-card
                                    v-if="largeVehicleCard && vehicle"
                                    :vehicle="vehicle"
                                    :exterior-color-code="exteriorColorCode"
                                    :options-cost="totalOptionsCost"
                                />

                                <small-vehicle-card
                                    v-if="!largeVehicleCard && vehicle"
                                    :vehicle="vehicle"
                                    :exterior-color-code="exteriorColorCode"
                                    :options-cost="totalOptionsCost"
                                />

                                <div
                                    v-if="vehicle"
                                    class="d-sm-block d-none price-details"
                                >
                                    <div class="d-flex justify-content-between">
                                        <div class="label">
                                            &nbsp;
                                        </div>
                                        <div class="value">
                                            <small>MSRP</small>
                                        </div>
                                    </div>
                                    <div class="d-flex justify-content-between">
                                        <div class="label">
                                            Base Price
                                        </div>
                                        <div class="value">
                                            {{ baseMsrp | numeral("$0,0") }}
                                        </div>
                                    </div>
                                    <div class="d-flex justify-content-between">
                                        <div class="label">
                                            Options
                                        </div>
                                        <div class="value">
                                            {{ totalOptionsCost > 0 ? "+" : ""
                                            }}{{
                                                totalOptionsCost
                                                    | numeral("$0,0")
                                            }}
                                        </div>
                                    </div>
                                    <hr />
                                    <div
                                        class="d-flex justify-content-between total"
                                    >
                                        <div class="label">
                                            <strong>Total</strong>
                                        </div>
                                        <div class="value text-right">
                                            <strong>{{
                                                totalCost | numeral("$0,0")
                                            }}</strong
                                            ><br /><small
                                                >Plus taxes &amp; fees.</small
                                            >
                                        </div>
                                    </div>
                                </div>

                                <div class="text-center my-4 d-sm-block d-none">
                                    <div class="w-100">
                                        <b-button
                                            variant="orange"
                                            :disabled="disabled"
                                            class="mb-2 mx-auto px-4"
                                            @click="next"
                                        >
                                            {{ nextLabel }}
                                        </b-button>
                                        <b-link
                                            v-if="!lastStep"
                                            @click="gotoVehiclePage"
                                        >
                                            I'm done. Skip to Summary
                                        </b-link>
                                    </div>
                                </div>
                            </affix>
                        </b-col>
                        <b-col sm="7" class="left-content py-3 py-md-0">
                            <slot />
                        </b-col>
                    </b-row>

                    <div
                        v-if="!lastStep"
                        class="bottom-actions-lg d-flex flex-column d-sm-none"
                    >
                        <b-row class="d-flex p-2 justify-content-center">
                            <b-button
                                variant="orange"
                                :disabled="disabled"
                                class="mx-auto px-4"
                                @click="next"
                            >
                                {{ nextLabel }}
                            </b-button>
                        </b-row>
                        <b-row class="d-flex justify-content-center">
                            <b-link
                                @click="gotoVehiclePage"
                                class="mx-auto px-4"
                            >
                                I'm done. Skip to Summary
                            </b-link>
                        </b-row>
                    </div>
                    <div v-else class="bottom-actions-sm d-flex flex-column justify-content-center d-sm-none">
                        <b-button
                            variant="orange"
                            :disabled="disabled"
                            class="mx-auto px-4"
                            @click="next"
                        >
                            {{ nextLabel }}
                        </b-button>
                    </div>
                </b-col>
            </b-row>

            <user-confirmation-modal />
            <user-choice-needed-modal />
        </b-container>
    </div>
</template>

<style lang="scss" scoped>
.bottom-actions-sm {
    position: fixed;
    bottom: 0;
    right: 0;
    z-index: 10;
    width: 100%;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.5);
    background-color: $very-light-gray;
    height: 55px;
}

.bottom-actions-lg {
    position: fixed;
    bottom: 0;
    right: 0;
    z-index: 10;
    width: 100%;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.5);
    background-color: $very-light-gray;
    height: 75px;
}

.breadcrumb {
    padding: 0.75rem 0;
}

.price-details {
    font-size: 16px;

    small {
        font-size: 10px;
    }

    strong {
        color: $primary-blue;
        font-weight: normal;
    }
}

@include media-breakpoint-up(md) {
    .vehicle-details {
        border-left: 1px solid $muted-gray;
    }

    .main-content {
        padding-top: 20px;
        padding-bottom: 20px;
        border-top: 1px solid $muted-dark-gray;
    }

    .total {
        .value {
            line-height: 16px;
        }
    }
}

h1 {
    font-size: 24px;
    font-weight: normal;

    @include media-breakpoint-up(md) {
        font-size: 28px;
    }
}
</style>

<script>
import _ from "lodash";
import { get } from "vuex-pathify";
import VehicleCard from "../components/VehicleCard";
import SmallVehicleCard from "../components/VehicleCard/SmallVehicleCard";
import VehicleCardLoader from "Modules/Configure/components/VehicleCard/VehicleCardLoader";
import UserConfirmationModal from "Modules/Configure/components/Modal/UserConfirmationModal";
import UserChoiceNeededModal from "Modules/Configure/components/Modal/UserChoiceNeededModal";
import PickPriceDrive from "Components/PickPriceDrive/index";

export default {
    components: {
        PickPriceDrive,
        UserConfirmationModal,
        UserChoiceNeededModal,
        VehicleCardLoader,
        VehicleCard,
        SmallVehicleCard
    },

    props: {
        breadcrumbs: {
            required: true,
            type: Array
        },
        title: {
            required: true,
            type: String
        },
        largeVehicleCard: {
            required: false,
            type: Boolean,
            default: false
        },
        disabled: {
            required: false,
            type: Boolean,
            default: false
        },
        nextTitle: {
            required: false,
            type: String,
            default: null
        }
    },

    computed: {
        vehicle: get("configure/vehicle"),
        vehicleDetailsUrl: get("configure/vehicleDetailsUrl"),
        totalOptionsCost: get("configure/totalOptionsCost"),
        nextTabHeader: get("configure/nextTabHeader"),
        totalCost: get("configure/totalCost"),
        baseMsrp: get("configure/baseMsrp"),
        exteriorColorCode: get("configure/exteriorColorCode"),
        isLoading: get("configure/loading"),
        firstLoad: get("configure/firstLoad"),
        enableAffix() {
            return window.innerWidth >= 768;
        },
        affixWidth() {
            if (window.innerWidth >= 1440) {
                return "445px";
            } else if (window.innerWidth >= 1024) {
                return "390px";
            }

            return "290px";
        },
        nextLabel() {
            if (!_.isNil(this.nextTitle)) {
                return this.nextTitle;
            }

            if (_.isNil(this.nextTabHeader)) {
                return "Next Step | Summary";
            }

            return `Next Step | ${this.nextTabHeader}`;
        },
        lastStep() {
            return _.isNil(this.nextTabHeader);
        }
    },

    methods: {
        next() {
            this.$emit("next");
        },

        gotoVehiclePage() {
            document.location = this.vehicleDetailsUrl;
        }
    }
};
</script>
